import {
    buildOrgDetailPath,
    ORG_LIST_SLUG,
    parseOrgIdFromPathname,
} from '../features/superadmin/organizations/orgRoute';
import { Page } from '../types';

export const PAGE_TO_SLUG: Record<Page, string> = {
    'dashboard': 'dashboard',
    'kanban': 'negociacoes',

    'subscription': 'assinatura',
    'onboarding': 'onboarding',
    'profile': 'perfil',
    'monitoring': 'utilizacao',
    'whatsapp': 'whatsapp',
    'team': 'equipe',
    'billing': 'faturamento',
    'tracking': 'integracoes',
    'contacts': 'contatos',
    'products': 'produtos',
    'create-funnel': 'criar-funil',
    'edit-funnel': 'editar-funil',
    'chat': 'conversas',
    'connections': 'canais-de-atendimento',
    'settings': 'configuracoes-gerais',
    'settings-tags': 'tags',
    'settings-quick-replies': 'respostas-rapidas',
    'settings-pix': 'chave-pix',
    'settings-loss-reasons': 'motivos-perda',
    'settings-origins': 'origens',
    'settings-webhooks': 'webhooks',
    'settings-auto-movement': 'movimentacao-automatica',
    'follow-up': 'follow-up',
    'tasks': 'tarefas',
    'support': 'support',
    'parceiro': 'parceiro',
    'parceiro-aplicar': 'parceiro/aplicar',
    'superadmin': 'superadmin',
    'superadmin-organizations': 'superadmin-organizacoes',
    'superadmin-organization-detail': 'superadmin-organizacoes',
    'superadmin-users': 'superadmin-usuarios',
    'superadmin-finance': 'superadmin-financeiro',
    'superadmin-invoices': 'superadmin-faturas',
    'superadmin-whatsapp': 'superadmin-canais-whatsapp',
    'superadmin-integrations': 'superadmin-integracoes',
    'superadmin-communications': 'superadmin-comunicacoes',
    'superadmin-tickets': 'superadmin-tickets',
    'superadmin-comms-whatsapp': 'superadmin-comunicacao-whatsapp',
    'superadmin-partners': 'superadmin-parceiros',
};

export const SLUG_TO_PAGE: Record<string, Page> = Object.entries(PAGE_TO_SLUG).reduce((acc, [page, slug]) => {
    // Detail shares list slug; resolved via parseOrgIdFromPathname + getRouteStateFromPathname.
    if (page === 'superadmin-organization-detail') {
        return acc;
    }
    acc[slug] = page as Page;
    return acc;
}, {} as Record<string, Page>);

export const getSlugFromPage = (page: Page): string => {
    return PAGE_TO_SLUG[page] || page;
};

export const getPageFromSlug = (slug: string): Page | undefined => {
    // Handle exact matches
    if (SLUG_TO_PAGE[slug]) {
        return SLUG_TO_PAGE[slug];
    }

    // Fallback: check if the slug is actually a raw Page key (for backward compatibility)
    // We cast to check existence in the keys
    const pageKeys = Object.keys(PAGE_TO_SLUG) as Page[];
    if (pageKeys.includes(slug as Page)) {
        return slug as Page;
    }

    return undefined;
};

const AUTH_PATHS = new Set(['/login', '/register', '/registro', '/convite']);

export function isAuthPath(pathname: string): boolean {
    return AUTH_PATHS.has(pathname) || pathname.startsWith('/auth/mfa-');
}

/** Public partner candidature — available without session (create account + apply). */
export function isPartnerApplyRoute(pathname: string = typeof window !== 'undefined' ? window.location.pathname : ''): boolean {
    const normalized = pathname.replace(/\/$/, '') || '/';
    return normalized === '/parceiro/aplicar';
}

/** UI routes that require super_admin (authorization remains on Edge Functions). */
export function isSuperadminPage(page: Page | string): boolean {
    return page.startsWith('superadmin');
}

/** Configurações e integrações restritas a owner/admin (members usam chat, kanban, etc.). */
export const ADMIN_ONLY_PAGES: readonly Page[] = [
    'team',
    'connections',
    'tracking',
    'monitoring',
    'products',
    'whatsapp',
    'settings-tags',
    'settings-quick-replies',
    'settings-pix',
    'settings-origins',
    'settings-loss-reasons',
    'settings-auto-movement',
    'settings-webhooks',
];

export function isAdminOnlyPage(page: Page | string): boolean {
    return (ADMIN_ONLY_PAGES as readonly string[]).includes(page);
}

/** Partner portal — escopo do usuário, não da org (liberado mesmo sem plano CRM). */
export const PARTNER_PAGES: readonly Page[] = ['parceiro', 'parceiro-aplicar'];

export function isPartnerPage(page: Page | string): boolean {
    return (PARTNER_PAGES as readonly string[]).includes(page);
}

/** Shell isolado do parceiro (fora do CRM). Apply fica no CRM. */
export function isPartnerShellPage(page: Page | string): boolean {
    return page === 'parceiro';
}

export type PageAccessOptions = {
    isSuperadmin?: boolean;
    isMember?: boolean;
};

/** Redirect users away from pages their role cannot access (client UX guard only). */
export function sanitizePageForAccess(
    page: Page,
    options: PageAccessOptions | boolean = {},
): Page {
    const opts: PageAccessOptions =
        typeof options === 'boolean' ? { isSuperadmin: options } : options;
    const isSuperadmin = opts.isSuperadmin ?? false;
    const isMember = opts.isMember ?? false;

    if (!isSuperadmin && isSuperadminPage(page)) return 'dashboard';
    if (isMember && isAdminOnlyPage(page)) return 'dashboard';
    return page;
}

/** Legacy / invalid localStorage page ids → safe Page (UI only, not authorization). */
export function normalizeStoredPage(saved: string | null | undefined): Page {
    if (!saved) return 'dashboard';
    if (saved === 'superadmin-users') return 'superadmin-organizations';
    if (saved === 'superadmin-invoices') return 'superadmin-finance';
    if ((Object.keys(PAGE_TO_SLUG) as string[]).includes(saved)) {
        return saved as Page;
    }
    return getPageFromSlug(saved) ?? 'dashboard';
}

/**
 * Post-auth / boot UI destination.
 * Keep current app path; otherwise restore from saved page; else dashboard.
 * Pure — not an authz check; backend remains source of truth.
 */
export function resolvePostAuthDestination(
    pathname: string,
    savedPage: string | null | undefined,
    options?: PageAccessOptions & { isSuperadmin?: boolean; returnPath?: string | null },
): { page: Page; path: string } {
    const normalized = pathname.replace(/\/$/, '') || '/';
    const access: PageAccessOptions = {
        isSuperadmin: options?.isSuperadmin ?? false,
        isMember: options?.isMember ?? false,
    };

    if (!isAuthPath(normalized) && normalized !== '/') {
        const { page } = getRouteStateFromPathname(normalized);
        const safePage = sanitizePageForAccess(page, access);
        const path = safePage === page
            ? normalized
            : `/${getSlugFromPage(safePage)}`;
        return { page: safePage, path };
    }

    const returnPath = options?.returnPath?.replace(/\/$/, '') || null;
    if (returnPath && returnPath !== '/' && !isAuthPath(returnPath)) {
        const { page } = getRouteStateFromPathname(returnPath);
        const safePage = sanitizePageForAccess(page, access);
        const path = safePage === page
            ? returnPath
            : `/${getSlugFromPage(safePage)}`;
        return { page: safePage, path };
    }

    const page = sanitizePageForAccess(normalizeStoredPage(savedPage), access);
    return { page, path: `/${getSlugFromPage(page)}` };
}

export type AppRouteState = {
    page: Page;
    superadminOrgId: string | null;
};

/** Resolve SPA page + superadmin org context from a pathname (no window). */
export function getRouteStateFromPathname(pathname: string): AppRouteState {
    const normalizedPathname = pathname.replace(/\/$/, '') || pathname;
    const cleanPath = normalizedPathname.replace(/^\//, '').split('?')[0];

    if (
        cleanPath === 'register' ||
        cleanPath === 'registro' ||
        cleanPath === 'login' ||
        cleanPath === 'convite' ||
        cleanPath.startsWith('auth/mfa-') ||
        !cleanPath
    ) {
        return { page: 'dashboard', superadminOrgId: null };
    }

    if (cleanPath === 'superadmin-usuarios') {
        return { page: 'superadmin-organizations', superadminOrgId: null };
    }

    if (cleanPath === 'superadmin-faturas') {
        return { page: 'superadmin-finance', superadminOrgId: null };
    }

    const orgId = parseOrgIdFromPathname(normalizedPathname);
    if (orgId) {
        return { page: 'superadmin-organization-detail', superadminOrgId: orgId };
    }

    if (cleanPath === ORG_LIST_SLUG) {
        return { page: 'superadmin-organizations', superadminOrgId: null };
    }

    if (cleanPath === 'parceiro/aplicar') {
        return { page: 'parceiro-aplicar', superadminOrgId: null };
    }

    return { page: getPageFromSlug(cleanPath) ?? 'dashboard', superadminOrgId: null };
}

export type AppPathChange = { path: string; mode: 'push' | 'replace' };

export type ResolveAppPathOptions = {
    superadminOrgId?: string | null;
};

/** Decide se/como atualizar a URL ao ir para uma página do app. */
export function resolveAppPathChange(
    currentPathname: string,
    page: Page,
    options?: ResolveAppPathOptions,
): AppPathChange | null {
    let path: string;

    if (page === 'superadmin-organization-detail') {
        const orgId = options?.superadminOrgId;
        path = orgId ? buildOrgDetailPath(orgId) : `/${getSlugFromPage('superadmin-organizations')}`;
    } else if (page === 'superadmin-users') {
        path = `/${getSlugFromPage('superadmin-organizations')}`;
        if (currentPathname === path) return null;
        return { path, mode: 'replace' };
    } else if (page === 'superadmin-invoices') {
        path = `/${getSlugFromPage('superadmin-finance')}`;
        if (currentPathname === path) return null;
        return { path, mode: 'replace' };
    } else {
        path = `/${getSlugFromPage(page)}`;
    }

    if (currentPathname === path) return null;
    // Sair de /login (pós-auth) deve atualizar o slug; replace evita voltar ao login no back.
    if (isAuthPath(currentPathname)) {
        return { path, mode: 'replace' };
    }
    return { path, mode: 'push' };
}
